/**
 * The orders a business's line was bought for, under the line on the review screen and on a
 * confirmed receipt.
 *
 * Most lines are for no order in particular, so the control is one small link until used.
 * Ticking orders splits the line between them equally; typing an amount fixes that order's
 * share and the rest is split among the others. Saving replaces what the line was attached to.
 */

import { useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ClipboardList } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/api/problem";
import { Button, Input, Notice } from "@/components/ui/Field";
import { OrderStatusBadge } from "@/features/orders/OrderBits";
import { businessOrdersQuery } from "@/features/orders/queries";
import type { OrderStatus } from "@/features/orders/lifecycle";
import { formatMoney } from "@/lib/money";
import { orderChoices, shares, toAllocationsBody, type Pick } from "./allocation";
import { Amount } from "./ReceiptBits";
import { useSetAllocations, type Allocation } from "./queries";
import type { EditLine } from "./review";
import { amountInput } from "./schemas";

export function LineOrders({
  receiptId,
  line,
  allocations,
  disabled,
}: {
  receiptId: string;
  line: EditLine;
  allocations: Allocation[];
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);

  if (editing && line.business_id) {
    return (
      <OrderPicker
        receiptId={receiptId}
        line={line}
        businessId={line.business_id}
        allocations={allocations}
        onDone={() => setEditing(false)}
      />
    );
  }

  return (
    <div className="space-y-1">
      {allocations.length > 0 && (
        <ul className="space-y-1">
          {allocations.map((allocation) => (
            <li key={allocation.id} className="flex items-center gap-2 text-sm text-stone-600">
              <ClipboardList className="h-4 w-4 shrink-0 text-stone-400" aria-hidden />
              <span className="min-w-0 flex-1 truncate">
                {t("receipts.orders.for", {
                  order: allocation.order.description,
                  customer: allocation.order.customer_name,
                })}
              </span>
              <Amount money={allocation.amount} />
            </li>
          ))}
        </ul>
      )}
      <button
        type="button"
        disabled={disabled}
        className="text-brand-700 inline-flex items-center gap-1 text-sm underline disabled:opacity-50"
        onClick={() => setEditing(true)}
      >
        {allocations.length === 0 && <ClipboardList className="h-4 w-4" aria-hidden />}
        {allocations.length > 0 ? t("receipts.orders.change") : t("receipts.orders.attach")}
      </button>
    </div>
  );
}

interface Choice {
  id: string;
  description: string;
  customerName: string;
  status: OrderStatus | null;
}

function OrderPicker({
  receiptId,
  line,
  businessId,
  allocations,
  onDone,
}: {
  receiptId: string;
  line: EditLine;
  businessId: string;
  allocations: Allocation[];
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const orders = useInfiniteQuery(businessOrdersQuery(businessId));
  const save = useSetAllocations(receiptId);
  const currency = line.line_total.currency;
  const [picks, setPicks] = useState<Pick[]>(() =>
    allocations.map((allocation) => ({
      orderId: allocation.order.id,
      amount: allocation.is_override ? amountInput(allocation.amount) : "",
    })),
  );

  const listed: Choice[] = orderChoices(orders.data?.pages.flatMap((page) => page.items) ?? []).map(
    (order) => ({
      id: order.id,
      description: order.description,
      customerName: order.customer.name,
      status: order.status,
    }),
  );
  // An attached order older than the pages loaded still shows, so unticking it is possible.
  const missing: Choice[] = allocations
    .filter((allocation) => !listed.some((choice) => choice.id === allocation.order.id))
    .map((allocation) => ({
      id: allocation.order.id,
      description: allocation.order.description,
      customerName: allocation.order.customer_name,
      status: null,
    }));
  const choices = [...missing, ...listed];
  const parts = shares(line.line_total.minor, picks, currency);
  const left = parts ? line.line_total.minor - parts.reduce((sum, part) => sum + part, 0) : 0;

  function toggle(orderId: string, on: boolean) {
    setPicks((current) =>
      on
        ? [...current, { orderId, amount: "" }]
        : current.filter((pick) => pick.orderId !== orderId),
    );
  }

  function setAmount(orderId: string, amount: string) {
    setPicks((current) =>
      current.map((pick) => (pick.orderId === orderId ? { ...pick, amount } : pick)),
    );
  }

  async function submit() {
    try {
      await save.mutateAsync({ lineId: line.id, body: toAllocationsBody(picks, currency) });
      onDone();
    } catch {
      // Shown from save.error below.
    }
  }

  return (
    <div className="space-y-3 rounded-lg bg-stone-50 p-3">
      <p className="text-sm font-medium text-stone-700">{t("receipts.orders.title")}</p>

      {orders.isPending && <p className="text-sm text-stone-500">{t("common.loading")}</p>}
      {orders.isError && <Notice tone="error">{t("orders.errors.loadFailed")}</Notice>}
      {orders.isSuccess && choices.length === 0 && (
        <p className="text-sm text-stone-600">
          {t("receipts.orders.none")}{" "}
          <Link to="/orders/new" className="text-brand-700 underline">
            {t("receipts.orders.newOrder")}
          </Link>
        </p>
      )}

      {choices.length > 0 && (
        <ul className="space-y-2">
          {choices.map((choice) => {
            const index = picks.findIndex((pick) => pick.orderId === choice.id);
            const pick = picks[index];
            const share = parts && index >= 0 ? parts[index] : undefined;
            return (
              <li key={choice.id} className="space-y-2">
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-5 w-5 shrink-0"
                    checked={pick !== undefined}
                    onChange={(event) => toggle(choice.id, event.target.checked)}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm text-stone-800">{choice.description}</span>
                    <span className="block text-xs text-stone-500">{choice.customerName}</span>
                  </span>
                  {choice.status && <OrderStatusBadge status={choice.status} />}
                </label>
                {pick && (
                  <div className="ml-8 flex items-center gap-2">
                    <Input
                      aria-label={t("receipts.orders.share", { order: choice.description })}
                      inputMode="decimal"
                      className="w-28"
                      value={pick.amount}
                      placeholder={
                        share === undefined ? "" : amountInput({ minor: share, currency })
                      }
                      onChange={(event) => setAmount(choice.id, event.target.value)}
                    />
                    <span className="text-xs text-stone-500">
                      {pick.amount.trim() === ""
                        ? t("receipts.orders.equalShare")
                        : t("receipts.orders.setShare")}
                    </span>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {orders.hasNextPage && (
        <button
          type="button"
          className="text-brand-700 text-sm underline disabled:opacity-50"
          disabled={orders.isFetchingNextPage}
          onClick={() => void orders.fetchNextPage()}
        >
          {t("orders.loadMore")}
        </button>
      )}

      {parts === null && <Notice tone="error">{t("receipts.orders.tooMuch")}</Notice>}
      {parts !== null && picks.length > 0 && left > 0 && (
        <p className="text-sm text-stone-600">
          {t("receipts.orders.left", { amount: formatMoney({ minor: left, currency }) })}
        </p>
      )}
      {save.isError && (
        <Notice tone="error">
          {save.error instanceof ApiError
            ? t("receipts.review.notSavedBecause", { reason: save.error.message })
            : t("receipts.errors.saveFailed")}
        </Notice>
      )}

      <div className="flex gap-2">
        <Button
          type="button"
          disabled={parts === null || save.isPending}
          onClick={() => void submit()}
        >
          {save.isPending ? t("common.saving") : t("common.save")}
        </Button>
        <Button type="button" variant="secondary" onClick={onDone}>
          {t("common.cancel")}
        </Button>
      </div>
    </div>
  );
}
